package com.solevo.dist.util

import com.solevo.dist.i18n.Localization
import kotlin.js.JsName
import kotlin.math.absoluteValue

/**
 * Centralized amount formatting utilities for the application.
 * Provides consistent amount formatting across all ViewModels and pages.
 * Matches Android formatPrice() implementation using Intl.NumberFormat.
 */
object AmountFormatter {
    
    /**
     * Gets currency symbol from currency code (matches Android getCurrencySymbol).
     */
    private fun getCurrencySymbol(currencyCode: String): String {
        val mapCurrency = mapOf(
            "XOF" to "F",
            "USD" to "$",
            "EUR" to "€",
            "XAF" to "F",
            "CFA" to "F"
        )
        return mapCurrency[currencyCode] ?: currencyCode
    }
    
    /**
     * Rounds value using HALF_UP rounding mode (matches Android DecimalFormat default behavior).
     * HALF_UP means: if the fractional part is >= 0.5, round up; otherwise round down.
     */
    private fun roundHalfUp(value: Double): Long {
        return if (value >= 0) {
            (value + 0.5).toLong()
        } else {
            (value - 0.5).toLong()
        }
    }
    
    /**
     * Formats number using locale-aware formatting (matches Android formatDouble).
     * Uses Intl.NumberFormat to match Android DecimalFormat behavior.
     * Android DecimalFormat uses HALF_UP rounding (rounds up on .5), so we use roundHalfUp()
     * to match this behavior exactly.
     */
    private fun formatDouble(value: Double, locale: String = Localization.language.value): String {
        // Round using HALF_UP to match Android DecimalFormat rounding behavior
        val roundedValue = roundHalfUp(value)
        val formatter = js("Intl.NumberFormat")(locale, js("{}"))
        return (formatter.format(roundedValue) as? String) ?: roundedValue.toString()
    }
    
    /**
     * Formats amount with currency and optional sign.
     * Matches Android formatPrice() implementation.
     * 
     * @param value The amount to format
     * @param currency Currency code (default: "XOF" which maps to "F")
     * @param showSign Whether to show sign for negative values (default: true)
     * @param showPositiveSign Whether to show "+" for positive values (default: false)
     * @param includeSpace Whether to include space before currency (default: true)
     * @return Formatted string like "123,456 F" or "123 456F" or "-123,456 FCFA"
     */
    fun formatAmount(
        value: Double,
        currency: String = "XOF",
        showSign: Boolean = true,
        showPositiveSign: Boolean = false,
        includeSpace: Boolean = true
    ): String {
        val formattedNumber = formatDouble(value.absoluteValue)
        val currencySymbol = getCurrencySymbol(currency)
        
        val sign = when {
            !showSign -> ""
            value < 0 -> "-"
            value > 0 && showPositiveSign -> "+"
            else -> ""
        }
        
        val space = if (includeSpace) " " else ""
        return "$sign$formattedNumber$space$currencySymbol"
    }
    
    /**
     * Formats amount without sign (absolute value).
     * Used in: Rewards, Statements summary
     * 
     * @param value The amount to format
     * @param currency Currency code (default: "XOF" which maps to "F")
     * @param includeSpace Whether to include space before currency (default: true)
     * @return Formatted string like "123,456 F"
     */
    fun formatAmountNoSign(
        value: Double,
        currency: String = "XOF",
        includeSpace: Boolean = true
    ): String {
        return formatAmount(value, currency, showSign = false, includeSpace = includeSpace)
    }
    
    /**
     * Formats amount with full currency name (e.g., "FCFA").
     * Used in: InvoiceDetails, TransactionDetails, QuoteDetails
     * 
     * @param value The amount to format
     * @param currency Full currency name (default: "FCFA")
     * @param showSign Whether to show sign for negative values (default: true)
     * @return Formatted string like "123 456 FCFA" or "-123 456 FCFA"
     */
    fun formatAmountWithCurrency(
        value: Double,
        currency: String = "FCFA",
        showSign: Boolean = true
    ): String {
        return formatAmount(value, currency, showSign = showSign, includeSpace = true)
    }
    
    /**
     * Formats amount for Orders/Quotes tables (compact format, no space before currency).
     * Used in: OrdersViewModel, QuotesViewModel
     * Matches Android formatPrice() for Orders/Quotes display.
     * 
     * @param value The amount to format
     * @param currency Currency code (default: "XOF" which maps to "F")
     * @return Formatted string like "123,456F" or "-123,456F"
     */
    fun formatAmountCompact(value: Double, currency: String = "XOF"): String {
        return formatAmount(value, currency = currency, showSign = true, includeSpace = false)
    }
    
    /**
     * Formats amount for Statements table (with positive sign support).
     * Used in: StatementsViewModel
     * 
     * @param value The amount to format
     * @param currency Currency code (default: "XOF" which maps to "F")
     * @return Formatted string like "+123,456 F" or "-123,456 F"
     */
    fun formatAmountWithPositiveSign(value: Double, currency: String = "XOF"): String {
        return formatAmount(value, currency = currency, showSign = true, showPositiveSign = true, includeSpace = true)
    }
    
    /**
     * Formats quantity/units with optional unit suffix.
     * Used in: OrderDetailsPage, QuoteDetailsViewModel
     * 
     * @param value The quantity to format
     * @param unit Optional unit suffix (e.g., "kg", "L")
     * @return Formatted string like "123,456 F" or "123,456 kg"
     */
    fun formatUnits(value: Double, unit: String? = null): String {
        val formatted = formatAmountNoSign(value, currency = "XOF", includeSpace = true)
        return if (unit.isNullOrBlank()) {
            formatted
        } else {
            formatted.replace(" F", "") + " $unit"
        }
    }
    
    /**
     * Formats price per unit with optional unit suffix.
     * Used in: OrderDetailsPage, QuoteDetailsViewModel
     * 
     * @param value The price to format
     * @param unit Optional unit suffix (e.g., "kg", "L")
     * @return Formatted string like "123 456 FCFA" or "123 456 FCFA / kg"
     */
    fun formatPricePerUnit(value: Double, unit: String? = null): String {
        val formatted = formatAmountWithCurrency(value, currency = "FCFA")
        return if (unit.isNullOrBlank()) {
            formatted
        } else {
            "$formatted / $unit"
        }
    }
}

